// Wave 11 creator dashboard, on a claimed game's page:
// - Updates: whether we can see this game's updates (accounts' developer view, `dev.updateDetection`, set by the
//   version detector), "add thrdm.json" when we can't (docs/sdk/thrdm-json.md), and the detected updates, each with
//   "Add notes" that posts them as a devlog (community), since accounts keeps only the detector's own notes.
// - Nominate: put the game forward for an editorial row (accounts, moderator queue).

import { api, ApiError } from "../api.ts";
import type { Game } from "../catalogue.ts";
import { clear, errorBox, fmtDate, h, section, toast } from "../dom.ts";
import { detectionText, manifestUrl, nominationError, parseNominations, parseUpdates, type GameUpdate, type Nomination } from "../listing.ts";
import { versionText } from "../rows.ts";

/** The `thrdm.json` a creator adds next to the game (only `version` is required). */
export const MANIFEST_EXAMPLE = '{ "version": "1.4.0", "notes": "New night track. Two more ghosts.", "released": "2026-10-20" }';

/** The devlog title for an update's notes. */
export function notesTitle(u: Pick<GameUpdate, "versionLabel" | "detectedAt">, title: string): string {
  const v = versionText(u.versionLabel);
  return v ? `What's new in ${title} ${v}` : `What's new in ${title} (${fmtDate(u.detectedAt)})`;
}

export function updatesPanel(g: Game): HTMLElement {
  return section(
    "Updates",
    async (body) => {
      const [dev, updates] = await Promise.all([
        api<Game>("accounts", `/catalogue/games/${encodeURIComponent(g.id)}?view=developer`, { auth: false }).catch(() => null),
        api<unknown>("accounts", `/catalogue/games/${encodeURIComponent(g.id)}/updates?limit=10`, { auth: false }).then(parseUpdates, () => [] as GameUpdate[]),
      ]);
      const d = detectionText(dev?.dev?.updateDetection);
      const where = manifestUrl(g.url);
      clear(
        body,
        h("p", { class: d.ok ? "" : "note" }, d.text),
        d.addManifest
          ? h(
              "div",
              { class: "manifest-help" },
              h("p", { class: "small" }, "Add thrdm.json: a small static file at ", where ? h("code", null, where) : "the folder your game is served from", ". No code, no SDK. We check it every few hours and announce a new ", h("code", null, "version"), " once we've seen it twice."),
              h("pre", { class: "code" }, h("code", null, MANIFEST_EXAMPLE)),
              h("p", { class: "dim small" }, "Only version is required. Notes are plain text, up to 2,000 characters; players see them as What's new."),
            )
          : null,
        updates.length
          ? h(
              "ul",
              { class: "reviews updates" },
              updates.map((u) => updateItem(g, u)),
            )
          : h("p", { class: "dim small" }, "No updates detected yet. When one is, it shows here and players who hearted the game hear about it."),
      );
    },
    { class: "panel" },
  );
}

function updateItem(g: Game, u: GameUpdate): HTMLElement {
  const form = notesForm(g, u);
  return h(
    "li",
    { class: "review" },
    h("div", { class: "review-head" }, h("strong", null, versionText(u.versionLabel) ?? "Update"), h("span", { class: "dim small" }, fmtDate(u.detectedAt))),
    u.notes ? h("p", { class: "notes" }, u.notes) : h("p", { class: "dim small" }, "No notes with this update."),
    h("details", null, h("summary", null, "Add notes"), form),
  );
}

/** Notes for a detected update become a devlog: everyone who hearted the game gets it in the app. */
function notesForm(g: Game, u: GameUpdate): HTMLElement {
  const err = h("div", { "aria-live": "polite" });
  const title = h("input", { required: true, minlength: 3, maxlength: 140, value: notesTitle(u, g.title).slice(0, 140), "aria-label": "Devlog title" });
  const text = h("textarea", { rows: 5, required: true, "aria-label": "What changed", placeholder: "What changed in this update" });
  text.value = u.notes ?? "";
  const form: HTMLFormElement = h(
    "form",
    {
      class: "stack",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        try {
          const r = await api<{ notified: number }>("community", `/games/${g.id}/threads`, { body: { kind: "devlog", title: title.value.trim(), body: text.value } });
          clear(form, h("p", { class: "small" }, `Posted as a devlog; ${r.notified} ${r.notified === 1 ? "player" : "players"} who hearted ${g.title} will see it.`));
          toast("Devlog posted");
        } catch (x) {
          clear(err, errorBox(x));
        }
      },
    },
    title,
    text,
    h("button", { type: "submit", class: "btn small" }, "Post as devlog"),
    err,
  );
  return form;
}

// ---- nominate ------------------------------------------------------------------------------------------------------

interface NominatableRow {
  id: string;
  title: string;
}

const STATUS: Record<string, [string, string]> = {
  pending: ["In review", "warn"],
  accepted: ["In the row", "ok"],
  rejected: ["Not this time", "bad"],
  withdrawn: ["Withdrawn", ""],
};

export function nominatePanel(g: Game): HTMLElement {
  const host = h("div");
  const load = () =>
    clear(
      host,
      section(
        "Nominate for a row",
        async (body) => {
          const [rowsRes, mine] = await Promise.all([
            api<{ rows?: NominatableRow[] }>("accounts", "/catalogue/nominatable", { auth: false }),
            api<unknown>("accounts", `/creators/me/games/${encodeURIComponent(g.id)}/nominations`).then(parseNominations),
          ]);
          const rows = (rowsRes.rows ?? []).filter((r) => r && typeof r.id === "string");
          const titleOf = (id: string) => rows.find((r) => r.id === id)?.title ?? id;
          const err = h("div", { "aria-live": "polite" });
          const select = h("select", { required: true, "aria-label": "Row" }, rows.map((r) => h("option", { value: r.id }, r.title)));
          const pitch = h("textarea", { rows: 3, maxlength: 500, "aria-label": "Why it fits (optional)", placeholder: "Why it fits this row (optional)" });
          const form = h(
            "form",
            {
              class: "stack",
              onsubmit: async (e: Event) => {
                e.preventDefault();
                try {
                  await api("accounts", `/creators/me/games/${encodeURIComponent(g.id)}/nominations`, { body: { listId: select.value, ...(pitch.value.trim() ? { pitch: pitch.value.trim() } : {}) } });
                  toast(`Nominated for ${titleOf(select.value)}`);
                  load();
                } catch (x) {
                  const text = x instanceof ApiError ? nominationError(x.code) : null;
                  clear(err, text ? h("p", { class: "error", role: "alert" }, text) : errorBox(x));
                }
              },
            },
            h("label", { class: "stack small" }, "Row", select),
            h("label", { class: "stack small" }, "Why it fits (optional)", pitch),
            h("button", { type: "submit", class: "btn small" }, "Nominate"),
            err,
          );
          clear(
            body,
            h("p", { class: "dim small" }, "Put your game forward for an editorial row. A moderator looks at every nomination; accepted games are pinned in the row."),
            rows.length ? form : h("p", { class: "dim" }, "No rows take nominations right now."),
            mine.length ? h("ul", { class: "nominations" }, mine.map((n) => nominationItem(n, titleOf(n.listId), load))) : null,
          );
        },
        { class: "panel" },
      ),
    );
  load();
  return host;
}

function nominationItem(n: Nomination, rowTitle: string, reload: () => void): HTMLElement {
  const [label, cls] = STATUS[n.status] ?? [n.status, ""];
  return h(
    "li",
    null,
    h("strong", null, rowTitle),
    " ",
    h("span", { class: `badge ${cls}` }, label),
    h("span", { class: "dim small" }, ` ${fmtDate(n.createdAt)}`),
    n.status === "pending"
      ? h(
          "button",
          {
            type: "button",
            class: "btn small ghost",
            onclick: async () => {
              try {
                await api("accounts", `/creators/me/nominations/${encodeURIComponent(n.id)}`, { method: "DELETE" });
                reload();
              } catch (x) {
                toast(x instanceof Error ? x.message : String(x));
              }
            },
          },
          "Withdraw",
        )
      : null,
  );
}
